<template>

    <AdminBlock>
        <div class="row align-items-center">
            <p class="mb-0">
                <span>
                    <strong>Driver:</strong>
                    {{ localMovement.driver?.name ?? 'Not Set' }}
                    ({{ localMovement.transported ? 'Transported' : 'Driven' }})
                </span>
                <span>
                    <strong>POC:</strong>
                    {{ localMovement.poc ? 'Yes' : 'No' }} |
                </span>
                <span>
                    <strong>POD:</strong>
                    {{ localMovement.pod ? 'Yes' : 'No' }} |
                </span>
                <span>
                    <strong>Status:</strong>
                    {{ movementStatus }} |
                </span>
                <span>
                    <strong>Estimated Mileage:</strong>
                    {{ estimatedMileage }}
                </span>
            </p>
        </div>
    </AdminBlock>

    <AdminBlock>
        <div class="d-flex align-items-center justify-content-end">
            <div v-if="canBeAborted && !hasPOC && !hasPOD" class="d-flex align-items-center">
                <button  @click="showAbortModal()" class="btn btn-sm btn-danger">
                    <i class="fa fa-times"></i>
                    Abort Here
                </button>
                <div
                    class="btn btn-sm ps-2"
                    data-bs-toggle="popover"
                    data-bs-placement="bottom"
                    data-bs-html="true"
                    data-bs-trigger="click"
                    title="Abort Here Help"
                    data-bs-content="<p class='mb-2'>Sets this Movements Job to Aborted at this Movement.</p><p class='mb-2'>This requires the job to be abortable and the current movement to have no POC or POD.</p> This sets the first Movements details back to match the Job.">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                </div>
            </div>
            <div v-if="canSetJobToCompleted" class="d-flex align-items-center">
                <button @click="setJobToCompleted" class="btn btn-success btn-sm">
                    <i class="fas fa-trophy"></i>
                    Set Completed
                </button>
                <div
                    class="btn btn-sm ps-2"
                    data-bs-toggle="popover"
                    data-bs-placement="bottom"
                    data-bs-html="true"
                    data-bs-trigger="click"
                    title="Set Completed Help"
                    data-bs-content="<p class='mb-2'>Sets this Movements Job to completed.</p> This requires the current movement and all movements afterwards to be unassigned. It also requires all previous movements to be assigned and have POD.">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                </div>
            </div>
            <div class="d-flex align-items-center" v-if="canFinishJobAtThisLocation">
                <button @click="finishJobAtThisLocation()" class="btn btn-success btn-sm">
                    <i class="fa fa-check" aria-hidden="true"></i>
                    Finish Job at this Location
                </button>
                <div
                    class="btn btn-sm ps-2"
                    data-bs-toggle="popover"
                    data-bs-placement="bottom"
                    data-bs-html="true"
                    data-bs-trigger="click"
                    title="Finish Job Help"
                    data-bs-content="<p class='mb-2'>This finishes the Job at this movement and deletes any future movements.</p> <p class='mb-2'>It then sets the Jobs delivery date and time to this Movements and sets the Job status to completed.</p> This requires all movements afterwards to be unassigned and have no POC, POD, Expenses, Delivery Note or Evidence. It also requires all previous movements to assigned and have POD.">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                </div>
            </div>
            <a :href="movementEditLink" class="btn btn-primary btn-sm me-2">
                <i class="fas fa-edit"></i>
                Edit Movement
            </a>
            <a v-if="canShowAssignDriver" :href="assignDriverLink" class="btn btn-warning btn-sm me-2">
                <i class="fas fa-car"></i>
                Assign to Driver
            </a>
            <button v-if="localMovement.driver_id" @click="unassignDriver" class="btn btn-warning btn-sm me-2">
                <i class="fas fa-user-slash"></i>
                Unassign Driver
            </button>
            <button @click="toggleTransportedOrDriven()" class="btn btn-secondary btn-sm">
                <i class="fas fa-car-side"></i>
                {{ localMovement.transported ? 'Set Driven' : 'Set Transported' }}
            </button>
        </div>
    </AdminBlock>

    <div class="row">
        <div class="col-6">
            <AdminBlock title="Route">
                <PlotRoute
                    :points="points"
                    :image-route="googleMap"
                    :remove-title="true"
                />
            </AdminBlock>
        </div>
        <div class="col-6">
            <AdminBlock title="Collection">
                <p class="mb-0">
                    <strong class="me-1">Date:</strong>
                    <span v-html="collectionDate"></span>
                </p>
                <p class="mb-0">
                    <strong>Location:</strong>
                    {{ collectionLocationWithCompany }}
                </p>
            </AdminBlock>

            <AdminBlock title="Delivery">
                <p class="mb-0">
                    <strong class="me-1">Date:</strong>
                    <span v-html="deliveryDate"></span>
                </p>
                <p class="mb-0">
                    <strong>Location:</strong>
                    {{ deliveryLocationWithCompany }}
                </p>
            </AdminBlock>

            <DriverNotesModal
                identifier="standard"
                :movement="localMovement"
                :driver-note="internalDriverNotes"
                :is-edit="true"
                @update:modelValue="internalDriverNotes = $event"
            />

            <AdminBlock title="Driver Notes">
                <template v-slot:options>
                    <button
                        type="button"
                        class="btn btn-sm btn-secondary"
                        @click="showDriverModal"
                    >
                        Edit
                    </button>
                </template>
                <template v-if="internalDriverNotes && internalDriverNotes.length > 0">
                    {{ internalDriverNotes }}
                </template>
                <template v-else>
                    <p class="mb-0">No driver notes have been added yet.</p>
                </template>
            </AdminBlock>
            <p><a target="_BLANK" :href="googleMapsDirectionsUrl">Get Directions From Google</a></p>
        </div>

        <div class="row">
            <div :class="{ 'col-md-8': hasLogs, 'col-md-12': !hasLogs }">
                <div class="row">
                    <div :class="{ 'col-md-12': hasLogs, 'col-md-6': !hasLogs }">
                        <AdminBlock title="Expenses">
                            <template v-slot:options>
                                <button
                                    type="button"
                                    class="btn btn-secondary btn-sm me-2"
                                    @click="showExpenseModal"
                                >
                                    Add New Expense
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-warning btn-sm"
                                    @click="showFuelModal"
                                >
                                    Add Fuel
                                </button>
                            </template>

                            <div class="row">
                                <div class="col-12">
                                    <template v-if="localExpenses.length > 0">
                                        <table class="table table-striped mb-0">
                                            <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Driver</th>
                                                <th>Label</th>
                                                <th>Total</th>
                                                <th>Evidence Received</th>
                                                <th>Recharge Client</th>
                                                <th style="width: 140px;">Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="expense in localExpenses" :key="expense.id" class="align-middle">
                                                <td>
                                                    {{ expense.id }}
                                                </td>
                                                <td>
                                                    <a v-if="expense.driver" :href="getDriverLink(expense.driver)">
                                                        {{ expense.driver.name }}
                                                    </a>
                                                    <p v-else class="mb-0">
                                                        No Driver Assigned
                                                    </p>
                                                </td>
                                                <td>{{ expense.label }}</td>
                                                <td>({{ expense.fuel }} Litres) £{{ expense.total }}</td>
                                                <td>{{ expense.evidence_received ? 'Yes' : 'No' }}</td>
                                                <td>{{ expense.recharge_client ? 'Yes' : 'No' }}</td>
                                                <td class="align-middle">
                                                    <button class="btn btn-sm btn-secondary" @click="showEditExpenseModal(expense)">Edit</button>
                                                    <button class="btn btn-sm btn-danger ms-2" @click="deleteExpense(expense)">Delete</button>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </template>
                                    <template v-else>
                                        <p class="mb-0">No expenses have been added yet.</p>
                                    </template>
                                </div>
                            </div>

                        </AdminBlock>
                    </div>
                    <div :class="{ 'col-md-12': hasLogs, 'col-md-6': !hasLogs }">
                        <AdminBlock title="Evidence">
                            <template v-slot:options>
                                <template v-if="localMovement.driver">
                                    <button
                                        v-if="!hasPOC"
                                        type="button"
                                        class="btn btn-secondary btn-sm me-2"
                                        @click="showPocEvidenceModal"
                                    >
                                        Add POC Evidence
                                    </button>

                                    <button
                                        v-if="hasPOC && !hasPOD"
                                        type="button"
                                        class="btn btn-secondary btn-sm me-2"
                                        @click="showPodEvidenceModal"
                                    >
                                        Add POD Evidence
                                    </button>
                                </template>

                                <button
                                    v-if="!hasDeliveryNote"
                                    type="button"
                                    class="btn btn-warning btn-sm"
                                    @click="showDeliveryNoteModal"
                                >
                                    Add Delivery Note
                                </button>
                            </template>

                            <template v-if="localEvidence.length > 0 || localDeliveryNote">
                                <table class="table table-striped mb-0">
                                    <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Action</th>
                                        <th>Damage</th>
                                        <th>Mileage</th>
                                        <th>Files</th>
                                        <th style="width: 80px;">Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <template v-if="localEvidence.length > 0">
                                        <tr v-for="evidence in localEvidence" :key="evidence.id">
                                            <td class="align-middle">{{ evidence.type.toUpperCase() }}</td>
                                            <td class="align-middle">{{ evidence.action }}</td>
                                            <td class="align-middle">{{ evidence.damage }}</td>
                                            <td class="align-middle">{{ evidence.mileage }}</td>
                                            <td class="align-middle">
                                                <template v-if="evidence.files && evidence.files.length > 0">
                                                    {{ evidence.files.length }} {{ evidence.files.length === 1 ? 'File' : 'Files' }}
                                                </template>
                                                <template v-else>
                                                    <p class="mb-0">No Evidence</p>
                                                </template>
                                            </td>
                                            <td class="d-flex justify-content-end">
                                                <button class="btn btn-sm btn-secondary" @click="showEditEvidenceModal(evidence)">Edit</button>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-if="localDeliveryNote" :key="localDeliveryNote.id" class="align-middle">
                                        <td>Delivery Note</td>
                                        <td colspan="3">{{ localDeliveryNote.reference }}</td>
                                        <td>
                                            <template v-if="localDeliveryNote.files">
                                                {{ localDeliveryNote.files.length }} File{{ localDeliveryNote.files.length > 1 ? 's' : '' }}
                                            </template>
                                            <template v-else>
                                                <p class="mb-0">No Files</p>
                                            </template>
                                        </td>
                                        <td class="d-flex justify-content-end">
                                            <button
                                                class="btn btn-sm btn-secondary"
                                                @click="showDeliveryNoteModal('edit')"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </template>
                            <template v-else>
                                <p class="mb-0">No Evidence has been added yet.</p>
                            </template>

                        </AdminBlock>
                    </div>
                </div>
            </div>
            <div v-if="hasLogs" class="col-md-4">
                <AdminBlock title="Audit">
                    <table class="table table-striped mb-0">
                        <thead>
                        <tr>
                            <th>Time</th>
                            <th>Entry</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="log in logs" :key="log.id">
                            <td>{{ log.created_at }}</td>
                            <td>{{ log.entry }}</td>
                        </tr>
                        </tbody>
                    </table>
                </AdminBlock>
            </div>
        </div>
    </div>

    <AdminBlock>
        <a :href="jobLink" class="btn btn-secondary btn-sm">
            Back to Job {{ job.reference }}
        </a>
    </AdminBlock>

    <AbortModal
        :movement="localMovement"
        identifier="standard"
        @job-aborted="updateEvidence"
    />

    <EvidenceModal
        :movement="localMovement"
        identifier="poc"
        type="poc"
        @evidence-created="updateEvidence"
    />

    <EvidenceModal
        :movement="localMovement"
        identifier="pod"
        type="pod"
        @evidence-created="updateEvidence"
    />

    <EvidenceModal
        :job="job"
        :movement="localMovement"
        :evidence="(localMovement.evidence ?? []).find(e => e.id === currentEditEvidence?.id) || null"
        identifier="edit"
        :type="currentEditEvidence?.type"
        @evidence-created="editEvidence"
        @file-deleted="editEvidence"
    />

    <DeliveryNoteModal
        :movement="localMovement"
        identifier="standard"
        @delivery-note-created="updateDeliveryNotes"
        @file-deleted="removeDeliveryNoteFile"
    />

    <ExpenseModal
        identifier="standard"
        :movements="[localMovement]"
        :job="job"
        @expense-created="updateExpenses"
    />

    <ExpenseModal
        identifier="edit"
        :movements="[localMovement]"
        :job="job"
        :expense="currentEditExpense"
        @expense-created="updateExpenses"
        @file-deleted="updateExpenses"
    />

    <FuelModal
        identifier="standard"
        :movements="[localMovement]"
        :job="job"
        @expense-created="updateExpenses"
    />

</template>

<script setup>

import axios from "axios";
import Modal from "bootstrap/js/dist/modal";
import { ref, onMounted, computed, watch } from "vue";
import { errorAlert, successAlert } from "@/utils";
import AdminBlock from "@/components/movements/components/AdminBlock.vue";
import AbortModal from "@/components/movements/movements/AbortModal.vue";
import DriverNotesModal from "@/components/movements/movements/DriverNotesModal.vue";
import DeliveryNoteModal from "@/components/movements/movements/DeliveryNoteModal.vue";
import ExpenseModal from "@/components/movements/movements/ExpenseModal.vue";
import EvidenceModal from "@/components/movements/movements/EvidenceModal.vue";
import FuelModal from "@/components/movements/movements/FuelModal.vue";
import PlotRoute from "@/components/movements/components/PlotRoute.vue";

const props = defineProps({
    movement: {
        type: Object,
        required: true,
    },
    movementStatus : {
        type: String,
        required: true,
    },
    expenses: {
        type: Array,
        default: () => []
    },
    fuel: {
        type: Array,
        default: () => []
    },
    collectionDate: {
        type: String,
        default: '',
    },
    deliveryDate: {
        type: String,
        default: '',
    },
    driverNotes: {
        type: String,
        default: '',
    },
    googleMap: {
        type: String,
        default: '',
    },
    points: {
        type: Array,
        required: true
    },
    canBeAborted: {
        type: Boolean,
        default: false,
    }
})

const localMovement = ref(props.movement);
const job = computed(() => localMovement.value.job);

const estimatedMileage = computed(() => localMovement.value?.mileage ?? job.value?.mileage);

watch(() => props.movement, (value) => {
    localMovement.value = value;
}, {deep: true});

const abortModal = ref(null);
const localExpenses = ref(localMovement.value?.expenses ?? []);
const expenseModal = ref(null);
const editExpenseModal = ref(null);
const fuelModal = ref(null);
const driverNotesModal = ref(null);
const deliveryNoteModal = ref(null);
const PocEvidenceModal = ref(null);
const PodEvidenceModal = ref(null);
const EditEvidenceModal = ref(null);
const internalDriverNotes = ref(localMovement.value.driver_notes);
const logs = ref([]);

const localEvidence = ref(localMovement.value?.evidence ?? []);

watch(() => localMovement.value?.evidence, (ev) => {
    localEvidence.value = ev ?? [];
}, { immediate: true });

const currentEditEvidence = ref(null);
const currentEditExpense = ref(null);

const localDeliveryNote = computed(() => localMovement.value?.delivery_note ?? null);
const hasDeliveryNote = computed(() => !!localDeliveryNote.value);

const hasLogs = computed(() => logs.value?.length > 0);

const googleMapsDirectionsUrl = computed(() => {
    if (localMovement.value.collection_location && localMovement.value.delivery_location) {
        return `https://www.google.co.uk/maps/dir/${encodeURIComponent(localMovement.value.collection_location)}/${encodeURIComponent(localMovement.value.delivery_location)}`;
    }
    return '#';
});

const hasPOC = computed(() => localEvidence.value.some(e => e.type === 'poc'));
const hasPOD = computed(() => localEvidence.value.some(e => e.type === 'pod'));

const poc = computed(() => {
    return localEvidence.value.find(e => e.type === 'poc');
});

const pod = computed(() => {
    return localEvidence.value.find(e => e.type === 'pod');
});

// This should show on all movements but the last one
const canFinishJobAtThisLocation = computed(() => {
    const lastMovement = Array.isArray(job.value.movements) ? job.value.movements.at(-1) : null;
    return localMovement.value.id !== lastMovement.id;
});

const canSetJobToCompleted = computed(() => {
    return localMovement.value.driver_id === null && job.value.status !== 'completed';
});

const canShowAssignDriver = computed(() => {
    return !localMovement.value.driver_id && Date.parse(localMovement.value.collection_date) < Date.now();
});

const getLogs = function () {
    axios.get(route('api.movements.movements.logs', {movement: localMovement.value.id}))
        .then(response => {
            logs.value = response.data.data;
        })
        .catch(error => {
            errorAlert(error, "Error fetching movement logs.");
        });
};

onMounted(() => {
    abortModal.value = new Modal(document.getElementById('standard_abort_modal'));
    expenseModal.value = new Modal(document.getElementById('standard_expense_modal'));
    editExpenseModal.value = new Modal(document.getElementById('edit_expense_modal'));
    fuelModal.value = new Modal(document.getElementById('standard_fuel_modal'));
    driverNotesModal.value = new Modal(document.getElementById('standard_driver_note_modal'));
    deliveryNoteModal.value = new Modal(document.getElementById('standard_delivery_note_modal'));
    PocEvidenceModal.value = new Modal(document.getElementById('poc_evidence_modal'));
    PodEvidenceModal.value = new Modal(document.getElementById('pod_evidence_modal'));
    EditEvidenceModal.value = new Modal(document.getElementById('edit_evidence_modal'));

    // check if query parameter edit_driver_text is 1 and if show open the driver notes modal
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('edit_driver_text') === '1') {
        driverNotesModal.value.show();
    }

    getLogs();
});

const formatDate = function(dateString) {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
    return formatter.format(date);
}

const showAbortModal = function () {
    abortModal.value.show();
}

const showDriverModal = function () {
    driverNotesModal.value.show();
}

const showExpenseModal = function () {
    expenseModal.value.show();
}

const showFuelModal = function () {
    fuelModal.value.show();
}

const showDeliveryNoteModal = function () {
    deliveryNoteModal.value.show();
}

const showPocEvidenceModal = function () {
    PocEvidenceModal.value.show();
}

const showEditEvidenceModal = function (evidence) {
    currentEditEvidence.value = evidence;
    EditEvidenceModal.value.show();
}

const showEditExpenseModal = function (expense) {
    currentEditExpense.value = expense;
    editExpenseModal.value.show();
}

const showPodEvidenceModal = function () {
    PodEvidenceModal.value.show();
}

const updateExpenses = function ( incomingExpense ) {

    if (!incomingExpense || !incomingExpense.id) {
        console.error("Expense object or ID is missing", incomingExpense);
        return;
    }

    const index = localExpenses.value.findIndex(expense =>
        expense.id === incomingExpense.id
    );

    // If the expense is not found, it means it's a new expense, so we add it to the list
    if (index === -1) {
        // console.error("Could not find Expense with ID:", incomingExpense.id);
        localExpenses.value.push(incomingExpense);
        return;
    }

    // This replaces the existing expense with the incoming one
    localExpenses.value = [
        ...localExpenses.value.slice(0, index),
        incomingExpense,
        ...localExpenses.value.slice(index + 1)
    ];

    localMovement.value.expenses = [
        ...localExpenses.value.slice(0, index),
        incomingExpense,
        ...localExpenses.value.slice(index + 1)
    ];

    if (currentEditExpense.value.id === incomingExpense.id) {
      currentEditExpense.value = incomingExpense;
    }
}

/**
 * This is used when creating Evidence. It's only used with create POC and POD modals.
 * It simply pushes the new Evidence
 * @param value
 */
const updateEvidence = function ( value ) {
    localEvidence.value.push(value.evidence);
    localMovement.value.evidence = [...localEvidence.value];
}

/**
 * This manages updates from the update Evidence modal.
 * This is called when deleting files from evidence.
 * @param value
 */
const editEvidence = function (value) {
    if (!value.evidence || !value.evidence.id) {
        console.error("Evidence object or ID is missing", value);
        return;
    }

    const index = localEvidence.value.findIndex(evidence => evidence.id === value.evidence.id);
    if (index === -1) {
        console.error("Could not find evidence with ID:", value.evidence.id);
        return;
    }

    /*
     * This updates the localEvidence and localMovement evidence array with the new evidence object.
     * It has to happen in place so that deleting files shows in the modal without needing to reopen it.
     */
    localEvidence.value[index] = value.evidence;
    if (Array.isArray(localMovement.value.evidence)) {
        const movementIndex = localMovement.value.evidence.findIndex(evidence => evidence.id === value.evidence.id);
        if (movementIndex !== -1) {
            localMovement.value.evidence[movementIndex] = value.evidence;
        }
    }
}

const updateDeliveryNotes = (value) => {
    localMovement.value.delivery_note = value.delivery_note;
};

const removeDeliveryNoteFile = (value) => {
    localMovement.value.delivery_note = value.delivery_note;
};

const getDriverLink = function (driver) {
    return route('movements.drivers.show', { driver: driver.id });
}

const jobLink = computed(() => {
    return route('movements.jobs.show', { job: job.value.id });
});

const assignDriverLink = computed(() => {
    return route('movements.movements.assign', { movement: localMovement.value.id });
});

const movementEditLink = computed(() => {
    return route('movements.movements.edit', { movement: localMovement.value.id });
});

const toggleTransportedOrDriven = function () {
    axios.post(route('api.movements.movements.toggle-transported', {movement: localMovement.value.id}))
        .then(response => {
            localMovement.value.transported = response.data.transported;
            successAlert(`Movement successfull set Transported to ${localMovement.value.transported ? 'Transported' : 'Driven'}`, "Movement Updated");
        })
        .catch(error => {
            errorAlert(error, "Error toggling transported or driven.");
        });
}

const setJobToCompleted = function () {
    axios.get(route('api.movements.movements.set-job-to-completed', {movement: localMovement.value.id}))
        .then(response => {
            localMovement.value = response.data.movement;
            successAlert("Job has been set to completed.", "Job Completed");
        })
        .catch(error => {
            errorAlert(error, "Error setting job to completed.");
        });
}

const finishJobAtThisLocation = function() {
    axios.get(route('api.movements.movements.finish-job', {movement: localMovement.value.id}))
        .then(response => {
            localMovement.value = response.data.movement;
            successAlert("Job finished at this movement", "Success");
        })
        .catch(error => {
            errorAlert(error, "Error finishing job at this location.");
        });
}
/**
 * This is NOT for the POC and POC. It is only for deleting expenses.
 * @param expense The expense object to be deleted.
 */
const deleteExpense = function(expense) {
    if (!confirm('Are you sure you want to delete this expense?')) {
        return;
    }
    axios.delete(route('api.movements.expenses.destroy', {expense: expense.id}))
        .then(() => {
            successAlert('Expense Deleted', 'Success');
            localExpenses.value = localExpenses.value.filter(e => e.id !== expense.id);
            localMovement.value.expenses = localExpenses.value;
        })
        .catch(error => {
            errorAlert(error, 'Error Deleting Expense');
        });
}

const collectionLocationWithCompany = computed(() => {
    const company = localMovement.value.collection_company;
    const location = localMovement.value.collection_location;
    return company ? `${company}, ${location}` : location;
});

const deliveryLocationWithCompany = computed(() => {
    const company = localMovement.value.delivery_company;
    const location = localMovement.value.delivery_location;
    return company ? `${company}, ${location}` : location;
});

const unassignDriver = function () {
    axios.post(route('api.movements.movements.unassign', { movement: localMovement.value.id, driver: localMovement.value.driver.id }))
        .then(response => {
            localMovement.value.driver_id = null;
            localMovement.value.driver = null;
            successAlert('Driver unassigned.', 'Success');
            getLogs();
        })
        .catch(error => {
            errorAlert(error, 'Error unassigning driver.');
        });
}
</script>
